import { useEffect, useState } from "react";
import { Search, Activity, BookOpen, Layers, MessageSquare, Database, Eye, PenLine, ChevronLeft, ChevronRight, TrendingUp, TrendingDown, ArrowRight, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";

// Read-only inspection of the longitudinal intelligence corpus (Phase 2E endpoints under /api/admin/intelligence/*).

const TABS = [
  { key: "overview",        label: "Overview",        icon: Activity },
  { key: "inputs",          label: "Member inputs",   icon: PenLine },
  { key: "events",          label: "Events",          icon: BookOpen },
  { key: "continuity",      label: "Continuity",      icon: Layers },
  { key: "summaries",       label: "Summaries",       icon: Database },
  { key: "recommendations", label: "Recommendations", icon: MessageSquare },
  { key: "ontology",        label: "Archetype drift", icon: Activity },
  { key: "retrieval",       label: "Retrieval Debug", icon: Eye },
];
const PAGE = 10;
const PATH = { inputs: "inputs", events: "events", continuity: "continuity", summaries: "summaries", recommendations: "recommendations", ontology: "ontology-timeline", retrieval: "retrieval-debug" };
const ts = (v) => String(v || "").replace("T", " ").slice(0, 16);

function Kpi({ label, value, sub }) {
  return (
    <div className="bg-ivory border border-champagne rounded-md p-4">
      <p className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55">{label}</p>
      <p className="font-mono text-2xl text-cocoa mt-2">{value ?? "—"}</p>
      {sub && <p className="text-[11px] text-cocoa/55 mt-1">{sub}</p>}
    </div>
  );
}

function Pager({ page, onPage, testId }) {
  if (!page || page.total <= page.limit) return null;
  const cur = Math.floor(page.skip / page.limit) + 1;
  const pages = Math.max(1, Math.ceil(page.total / page.limit));
  return (
    <div className="mt-3 flex items-center justify-between gap-3 flex-wrap text-xs text-cocoa/60" data-testid={`${testId}-pager`}>
      <span data-testid={`${testId}-pager-label`}>Showing {page.skip + 1}–{Math.min(page.total, page.skip + page.limit)} of {page.total} · page {cur} of {pages}</span>
      <div className="flex gap-2">
        <button disabled={page.skip === 0} onClick={() => onPage(Math.max(0, page.skip - page.limit))} data-testid={`${testId}-pager-prev`} className="ss-pill ss-pill-ghost h-8 px-3 disabled:opacity-40"><ChevronLeft size={14} /> Prev</button>
        <button disabled={!page.has_more} onClick={() => onPage(page.skip + page.limit)} data-testid={`${testId}-pager-next`} className="ss-pill ss-pill-ghost h-8 px-3 disabled:opacity-40">Next <ChevronRight size={14} /></button>
      </div>
    </div>
  );
}

const Empty = ({ text }) => <p className="text-cocoa/55 text-sm">{text}</p>;
const Loading = () => <p className="text-cocoa/60 text-sm">Loading…</p>;

const INPUT_KIND = {
  ask: { label: "Ask SugarSense", tone: "bg-mauve/15 text-mauve" },
  draft: { label: "Message draft", tone: "bg-gold/25 text-cocoa" },
  tracker: { label: "Tracker entry", tone: "bg-sage/20 text-cocoa" },
  timeline: { label: "Timeline event", tone: "bg-champagne text-cocoa" },
  profile: { label: "Profile", tone: "bg-blush/40 text-cocoa" },
  calibration: { label: "Calibration", tone: "bg-cocoa/10 text-cocoa" },
};
const FIELD_LABEL = {
  what_happened: "What happened", user_goal: "Goal", his_recent_behaviour: "Their recent behaviour", relationship_stage: "Stage", scope: "Scope",
  intent: "Wanted to say", tone: "Tone", label: "Item", type: "Type", value_estimate: "Value", notes: "Notes", boundaries: "Boundaries", summary: "Summary",
  occurred_on: "Date", emotional_context: "Emotional context", promised: "Promised", delivered: "Delivered", alias: "Alias", stage: "Stage", partner_pronouns: "Pronouns",
  relationship_structure: "Structure", responses: "Answers", answers: "Answer count", version: "Version",
};
const fmtVal = (v) => typeof v === "boolean" ? (v ? "yes" : "no")
  : Array.isArray(v) ? v.map((x) => (x && typeof x === "object" ? `${x.question_id || x.id || ""}: ${x.value ?? x.answer ?? ""}` : String(x))).join(" · ")
  : v && typeof v === "object" ? JSON.stringify(v) : String(v);

function InputItem({ it }) {
  const k = INPUT_KIND[it.kind] || { label: it.kind, tone: "bg-champagne" };
  return (
    <li className="bg-ivory border border-champagne rounded-md p-3 text-[13px]" data-testid={`adm-intel-input-${it.id}`}>
      <div className="flex justify-between gap-3 flex-wrap">
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${k.tone}`}>{k.label}{it.alias ? ` · ${it.alias}` : ""}</span>
        <span className="text-cocoa/55 font-mono text-[11px]">{ts(it.at)}</span>
      </div>
      <dl className="mt-2 space-y-1.5">
        {Object.entries(it.fields || {}).map(([f, v]) => (
          <div key={f} className="grid grid-cols-[7rem_1fr] gap-2">
            <dt className="text-[10px] uppercase tracking-widest text-cocoa/50 pt-0.5">{FIELD_LABEL[f] || f.replace(/_/g, " ")}</dt>
            <dd className="text-cocoa/85 whitespace-pre-wrap break-words">{fmtVal(v)}</dd>
          </div>
        ))}
      </dl>
    </li>
  );
}

const DRIFT_TONE = { stable: "border-sage/60 bg-sage/10", shifting: "border-gold/60 bg-gold/10", volatile: "border-rose/60 bg-rose/10", early: "border-champagne bg-ivory", none: "border-champagne bg-ivory" };
const DRIFT_WORD = { stable: "Stable", shifting: "Shifting", volatile: "Volatile", early: "Early", none: "No data" };

function DriftCard({ d }) {
  if (!d) return null;
  return (
    <div className={`rounded-md border p-4 ${DRIFT_TONE[d.status] || DRIFT_TONE.none}`} data-testid="adm-intel-drift-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55">What this tab tells you</p>
        <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-ivory border border-champagne" data-testid="adm-intel-drift-status">{DRIFT_WORD[d.status] || d.status}</span>
      </div>
      <p className="mt-2 font-heading text-xl text-cocoa" data-testid="adm-intel-drift-headline">{d.headline}</p>
      <p className="mt-1 text-[13px] text-cocoa/75" data-testid="adm-intel-drift-detail">{d.detail}</p>
      {d.transitions?.length > 0 && (
        <ul className="mt-3 space-y-1 text-[12px] text-cocoa/75">
          {d.transitions.slice(-5).reverse().map((t, i) => (
            <li key={i} className="inline-flex items-center gap-1.5 flex-wrap"><span className="font-mono text-cocoa/50">{String(t.at || "").slice(0, 10)}</span> {t.from || "—"} <ArrowRight size={12} /> <strong>{t.to || "—"}</strong></li>
          ))}
        </ul>
      )}
      {d.divergence?.length > 0 && <p className="mt-2 inline-flex items-center gap-1.5 text-[12px] text-rose"><ShieldAlert size={12} /> {d.divergence.length} dimension(s) where calibration and observed behaviour disagree</p>}
      <p className="mt-3 text-[11px] text-cocoa/55 leading-relaxed">
        Every time SugarSense re-reads this member (after calibration or an advisor session) it records a snapshot of which archetype best fits her and how sure it is.
        A <strong>stable</strong> line means her advice is anchored; <strong>shifting</strong> or <strong>volatile</strong> means the read is still forming and recommendations should be treated as provisional.
      </p>
    </div>
  );
}

function StateRow({ st }) {
  const c = st.change;
  return (
    <li className="bg-ivory border border-champagne rounded-md p-3 text-[13px]" data-testid={`adm-intel-state-${st.id}`}>
      <div className="flex justify-between gap-3 flex-wrap">
        <span className="text-cocoa font-medium inline-flex items-center gap-2 flex-wrap">
          {st.primary_archetype || "—"}
          {c?.archetype_changed && <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-gold/25 text-cocoa">changed from {c.previous_archetype || "—"}</span>}
        </span>
        <span className="text-cocoa/55 font-mono text-[11px]">{ts(st.computed_at)}</span>
      </div>
      <p className="text-[11px] text-cocoa/60 mt-1 inline-flex items-center gap-1.5 flex-wrap">
        confidence {Math.round((st.confidence || 0) * 100)}%
        {c && c.confidence_delta !== 0 && (c.confidence_delta > 0
          ? <span className="inline-flex items-center gap-0.5 text-sage"><TrendingUp size={11} /> +{c.confidence_delta}</span>
          : <span className="inline-flex items-center gap-0.5 text-rose"><TrendingDown size={11} /> {c.confidence_delta}</span>)}
        · from calibration {Math.round((st.calibration_confidence || 0) * 100)}% · from behaviour {Math.round((st.longitudinal_confidence || 0) * 100)}% · {st.observed_signal_count ?? 0} signals
        {st.secondary_archetype && <> · also fits {st.secondary_archetype}</>}
      </p>
    </li>
  );
}

function BalancedGuidanceStats() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/intelligence/balanced-guidance/stats").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  const pct = (v) => (v == null ? "—" : `${Math.round(v * 100)}%`);
  return (
    <div className="mt-6" data-testid="adm-intel-bg-stats">
      <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Balanced guidance · {d.policy_version}</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
        <Kpi label="Sessions under policy" value={d.sessions} sub={`${d.capture?.sessions_missing_record_since_policy ?? 0} missing capture`} />
        <Kpi label="Clarification shown" value={pct(d.clarification_rate)} sub={`answered ${pct(d.answer_rate)} · skipped all ${pct(d.skip_all_rate)}`} />
        <Kpi label="Safety-first routes" value={(d.safety_levels?.elevated || 0) + (d.safety_levels?.possible || 0)} sub={`${d.user_harm_reported} own-conduct flags`} />
        <Kpi label="Corrections" value={d.corrections} sub={`${d.sessions_superseded} readings superseded`} />
      </div>
      <p className="mt-2 text-[11px] text-cocoa/55">Prompt runs by policy mode: {Object.entries(d.prompt_runs_by_policy_mode || {}).map(([k, v]) => `${k} ${v}`).join(" · ")}. Feedback "missed / wrong person": {Object.entries(d.feedback_missed_or_misattributed || {}).map(([k, v]) => `${k} ${v}`).join(" · ") || "none yet"}. {d.note}</p>
    </div>
  );
}

function GlobalEffectiveness() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/admin/intelligence/recommendations/effectiveness")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Couldn't load"));
  }, []);
  if (err) return <p className="text-rose text-sm">{err}</p>;
  if (!data) return <Loading />;
  const totals = data.totals || {};
  const th = "p-2 text-[11px] uppercase tracking-wider text-cocoa/65";
  return (
    <div className="mt-6">
      <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Cross-user recommendation effectiveness</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
        <Kpi label="All recommendations" value={totals.all} />
        <Kpi label="With outcome captured" value={totals.with_outcome} sub={`${Math.round((totals.outcome_coverage || 0) * 100)}% coverage`} />
        <Kpi label="Recommendation types" value={data.by_type?.length || 0} />
      </div>
      {data.by_type?.length > 0 && (
        <div className="mt-5">
          <p className="text-[11px] tracking-[0.3em] uppercase text-cocoa/55">By type</p>
          <div className="mt-2 border border-champagne rounded-md px-3 sm:px-0">
            <table className="w-full text-sm ss-table-stack">
              <thead className="bg-ivory"><tr>{["Type", "N", "Helpful", "Tried", "Not helpful", "Helpfulness"].map((h, i) => <th key={h} className={`${th} ${i ? "text-right" : "text-left"}`}>{h}</th>)}</tr></thead>
              <tbody>
                {data.by_type.map((r) => (
                  <tr key={r.recommendation_type} className="border-t border-champagne">
                    <td className="p-2 text-cocoa font-medium sm:font-normal">{r.recommendation_type}</td>
                    <td data-label="N" className="p-2 text-right font-mono text-cocoa">{r.n}</td>
                    <td data-label="Helpful" className="p-2 text-right font-mono text-sage">{r.helpful}</td>
                    <td data-label="Tried" className="p-2 text-right font-mono text-mauve">{r.tried}</td>
                    <td data-label="Not helpful" className="p-2 text-right font-mono text-rose">{r.not_helpful}</td>
                    <td data-label="Helpfulness" className="p-2 text-right font-mono text-cocoa">{Math.round((r.helpfulness_rate || 0) * 100)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {data.by_archetype?.length > 0 && (
        <div className="mt-5">
          <p className="text-[11px] tracking-[0.3em] uppercase text-cocoa/55">By archetype × type</p>
          <div className="mt-2 border border-champagne rounded-md px-3 sm:px-0">
            <table className="w-full text-sm ss-table-stack">
              <thead className="bg-ivory"><tr>{["Archetype", "Type", "N", "Helpful", "Rate"].map((h, i) => <th key={h} className={`${th} ${i > 1 ? "text-right" : "text-left"}`}>{h}</th>)}</tr></thead>
              <tbody>
                {data.by_archetype.slice(0, 20).map((r, i) => (
                  <tr key={i} className="border-t border-champagne">
                    <td className="p-2 text-cocoa font-medium sm:font-normal">{r.archetype_key}</td>
                    <td data-label="Type" className="p-2 text-cocoa/75">{r.recommendation_type}</td>
                    <td data-label="N" className="p-2 text-right font-mono">{r.n}</td>
                    <td data-label="Helpful" className="p-2 text-right font-mono text-sage">{r.helpful}</td>
                    <td data-label="Rate" className="p-2 text-right font-mono">{Math.round((r.helpfulness_rate || 0) * 100)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function UserInspection({ userId }) {
  const [activeTab, setActiveTab] = useState("inputs");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState({});
  const [err, setErr] = useState("");
  const [skip, setSkip] = useState({});
  const [inputKind, setInputKind] = useState("");

  const load = (key, url) => {
    setLoading((s) => ({ ...s, [key]: true }));
    api.get(url)
      .then((r) => setData((d) => ({ ...d, [key]: r.data })))
      .catch((e) => setErr(e?.response?.data?.detail || `Couldn't load ${key}`))
      .finally(() => setLoading((s) => ({ ...s, [key]: false })));
  };

  useEffect(() => {
    if (!userId) return;
    setData({}); setErr(""); setSkip({});
    load("overview", `/admin/intelligence/users/${userId}/overview`);
  }, [userId]);

  useEffect(() => {
    if (!userId || !PATH[activeTab]) return;
    const kindQ = activeTab === "inputs" && inputKind ? `&kind=${inputKind}` : "";
    load(activeTab, `/admin/intelligence/users/${userId}/${PATH[activeTab]}?skip=${skip[activeTab] || 0}&limit=${PAGE}${kindQ}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, userId, skip, inputKind]);

  const goPage = (key) => (sk) => setSkip((s) => ({ ...s, [key]: sk }));
  const list = (key, items, render, empty) => (
    loading[key] ? <Loading /> : (<>
      <ul className="space-y-2">{(items || []).map(render)}{(!items || items.length === 0) && <Empty text={empty} />}</ul>
      <Pager page={data[key]?.page} onPage={goPage(key)} testId={`adm-intel-${key}`} />
    </>)
  );

  if (!userId) return null;
  const ov = data.overview;

  return (
    <div className="mt-6">
      <div className="flex items-center gap-2 flex-wrap border-b border-champagne pb-3">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = activeTab === t.key;
          return (
            <button key={t.key} onClick={() => setActiveTab(t.key)} data-testid={`adm-intel-tab-${t.key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-[12px] rounded-md transition-all ${active ? "bg-mauve/15 text-cocoa border border-mauve" : "text-cocoa/65 hover:text-cocoa border border-transparent"}`}>
              <Icon size={12} /> {t.label}
            </button>
          );
        })}
      </div>

      {err && <p className="mt-3 text-rose text-sm">{err}</p>}

      <div className="mt-5">
        {activeTab === "overview" && (loading.overview ? <Loading /> : ov && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Kpi label="Events" value={ov.counts.relationship_events} />
              <Kpi label="Continuity" value={ov.counts.continuity_observations} />
              <Kpi label="Summaries" value={ov.counts.memory_summaries} />
              <Kpi label="Recommendations" value={ov.counts.recommendations} sub={`${ov.counts.recommendations_with_outcome} w/ outcome`} />
              <Kpi label="Behavioral signals" value={ov.counts.behavioral_signals} />
              <Kpi label="Ontology states" value={ov.counts.ontology_states} />
              <Kpi label="Embeddings" value={ov.counts.embeddings} sub={`${ov.counts.embeddings_vectorised} vectorised`} />
              <Kpi label="Calibration" value={ov.counts.calibration_responses} />
            </div>
            {ov.latest_ontology_state && (
              <div className="mt-5 bg-ivory border border-champagne rounded-md p-4">
                <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">Latest archetype read</p>
                <p className="mt-2 text-cocoa"><span className="font-medium">{ov.latest_ontology_state.primary_archetype || "—"}</span><span className="text-cocoa/55"> · confidence {Math.round((ov.latest_ontology_state.confidence || 0) * 100)}%</span></p>
                {Array.isArray(ov.latest_ontology_state.divergence) && ov.latest_ontology_state.divergence.length > 0 && <p className="mt-1 text-[12px] text-rose">{ov.latest_ontology_state.divergence.length} divergence flag(s)</p>}
              </div>
            )}
            {ov.latest_summary && (
              <div className="mt-3 bg-ivory border border-champagne rounded-md p-4">
                <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">Latest rolling summary (v{ov.latest_summary.version})</p>
                <pre className="mt-2 text-[12px] text-cocoa/85 leading-relaxed whitespace-pre-wrap font-mono">{ov.latest_summary.body}</pre>
              </div>
            )}
          </>
        ))}

        {activeTab === "inputs" && (
          <div data-testid="adm-intel-inputs">
            <p className="text-[12px] text-cocoa/65 bg-gold/10 border border-gold/30 rounded-md p-2.5 inline-flex items-start gap-2"><ShieldAlert size={14} className="shrink-0 mt-0.5 text-gold" /> Everything the member typed or logged herself — exactly as entered. Each view of this tab is written to the audit log.</p>
            <div className="mt-3 flex gap-2 flex-wrap">
              {[["", "All"], ...Object.entries(INPUT_KIND).map(([k, v]) => [k, v.label])].map(([k, l]) => (
                <button key={k} onClick={() => { setInputKind(k); setSkip((s) => ({ ...s, inputs: 0 })); }} data-testid={`adm-intel-input-kind-${k || "all"}`}
                  className={`ss-pill h-8 px-3 text-xs ${inputKind === k ? "ss-pill-primary" : "ss-pill-ghost"}`}>{l}{data.inputs?.counts && k && data.inputs.counts[k] != null ? ` · ${data.inputs.counts[k]}` : ""}</button>
              ))}
            </div>
            <div className="mt-3">{list("inputs", data.inputs?.items, (it) => <InputItem key={`${it.kind}-${it.id}`} it={it} />, "Nothing entered yet.")}</div>
          </div>
        )}

        {activeTab === "events" && list("events", data.events?.events, (e) => (
          <li key={e.id} className="bg-ivory border border-champagne rounded-md p-3 text-[13px]">
            <div className="flex justify-between gap-3"><span className="text-mauve font-medium">{e.event_type}</span><span className="text-cocoa/55 font-mono text-[11px]">sig {Math.round((e.significance_score || 0) * 100)}% · {e.emotional_valence}</span></div>
            <p className="mt-1 text-cocoa/85">{e.description}</p>
            <p className="mt-1 text-[11px] text-cocoa/45 font-mono">{e.occurred_at} · recurrence #{e.recurrence_index}</p>
          </li>
        ), "No events.")}

        {activeTab === "continuity" && list("continuity", data.continuity?.observations, (o) => (
          <li key={o.id} className="bg-ivory border border-champagne rounded-md p-3 text-[13px]">
            <div className="flex justify-between"><span className="text-cocoa font-medium">{o.human_label}</span><span className="text-cocoa/55 font-mono text-[11px]">{o.occurrences}× · conf {Math.round((o.confidence || 0) * 100)}%</span></div>
            <p className="mt-1 text-[11px] text-cocoa/45 font-mono">first {o.first_seen_at} → last {o.last_seen_at}</p>
          </li>
        ), "No continuity observations.")}

        {activeTab === "summaries" && list("summaries", data.summaries?.summaries, (s) => (
          <li key={s.id} className="bg-ivory border border-champagne rounded-md p-3">
            <div className="flex justify-between text-[11px] text-cocoa/55 font-mono"><span>v{s.version} · {s.events_covered} events</span><span>{s.generated_at}</span></div>
            <pre className="mt-2 text-[12px] text-cocoa/85 leading-relaxed whitespace-pre-wrap font-mono">{s.body}</pre>
          </li>
        ), "No summaries.")}

        {activeTab === "recommendations" && list("recommendations", data.recommendations?.recommendations, (r) => (
          <li key={r.id} className="bg-ivory border border-champagne rounded-md p-3 text-[13px]">
            <div className="flex justify-between gap-3"><span className="text-mauve font-medium">{r.recommendation_type}</span><span className="text-cocoa/55 font-mono text-[11px]">{r.outcome ? <span className="text-sage">outcome: {r.outcome}</span> : "no outcome yet"}</span></div>
            <p className="mt-1 text-cocoa/85">{r.body_anon}</p>
            <p className="mt-1 text-[11px] text-cocoa/45 font-mono">{r.generated_at} · archetype {r.context?.archetype_key || "—"} · phase {r.context?.phase || "—"}</p>
          </li>
        ), "No recommendations.")}

        {activeTab === "ontology" && (loading.ontology ? <Loading /> : (<>
          <DriftCard d={data.ontology?.drift} />
          <p className="mt-4 text-[10px] tracking-[0.3em] uppercase text-cocoa/55">Every recompute, newest first</p>
          <ul className="mt-2 space-y-2">
            {(data.ontology?.states || []).map((st) => <StateRow key={st.id} st={st} />)}
            {(!data.ontology?.states || data.ontology.states.length === 0) && <Empty text="No archetype reads yet." />}
          </ul>
          <Pager page={data.ontology?.page} onPage={goPage("ontology")} testId="adm-intel-ontology" />
        </>))}

        {activeTab === "retrieval" && list("retrieval", data.retrieval?.retrievals, (r) => (
          <li key={r.id} className="bg-ivory border border-champagne rounded-md p-3 text-[12px]">
            <div className="flex justify-between text-cocoa/55 font-mono"><span>{r.timestamp}</span><span>{r.engagement_signal || "—"}</span></div>
            <pre className="mt-1 text-cocoa/85 whitespace-pre-wrap font-mono text-[11px]">{JSON.stringify(r.items || r, null, 2)}</pre>
          </li>
        ), "No retrievals.")}
      </div>
    </div>
  );
}

export function AdminIntelligenceConsole() {
  const [search, setSearch] = useState("");
  const [hits, setHits] = useState([]);
  const [activeUser, setActiveUser] = useState(null);
  const [searching, setSearching] = useState(false);
  const [err, setErr] = useState("");

  const runSearch = async () => {
    if (!search.trim()) return;
    setSearching(true); setErr("");
    try {
      const r = await api.get(`/admin/users/lookup?q=${encodeURIComponent(search.trim())}`);
      const items = r.data?.items || r.data?.users || [];
      setHits(items.slice(0, 10));
      if (items.length === 0) setErr("No users matched that query.");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Lookup failed");
    } finally {
      setSearching(false);
    }
  };

  return (
    <div data-testid="admin-intel-console" className="space-y-6">
      <div>
        <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Intelligence inspection</p>
        <h2 className="font-heading text-2xl text-cocoa mt-1">Operational trust check</h2>
        <p className="text-cocoa/65 text-sm mt-1">Read-only inspection of the longitudinal intelligence corpus. Never mutates. Cross-user effectiveness is shown below; pick a user above to deep-dive.</p>
      </div>

      <div className="bg-ivory border border-champagne rounded-md p-4">
        <p className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55">Find a user</p>
        <p className="mt-1 text-[12px] text-cocoa/60">Search by email or name, then pick the member. The first tab, <strong>Member inputs</strong>, shows exactly what she typed — Ask entries, draft requests, tracker and timeline notes, profile notes and calibration answers.</p>
        <div className="flex gap-2 mt-2">
          <div className="relative flex-1 min-w-0">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-cocoa/40" />
            <input data-testid="adm-intel-search-input" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
              placeholder="email, name, or id" className="w-full bg-ivory border border-champagne rounded-md h-10 pl-9 pr-3 text-cocoa text-sm focus:border-mauve outline-none" />
          </div>
          <button onClick={runSearch} disabled={searching} data-testid="adm-intel-search-btn" className="ss-pill ss-pill-primary h-10 px-5 shrink-0 disabled:opacity-50">{searching ? "Searching…" : "Search"}</button>
        </div>
        {err && <p className="mt-2 text-rose text-[12px]">{err}</p>}
        {hits.length > 0 && (
          <ul className="mt-3 space-y-1">
            {hits.map((u) => (
              <li key={u.id}>
                <button onClick={() => setActiveUser(u)} data-testid={`adm-intel-pick-${u.id}`}
                  className={`w-full text-left p-2 rounded-md text-sm transition-colors ${activeUser?.id === u.id ? "bg-mauve/10 border border-mauve" : "border border-transparent hover:bg-champagne/40"}`}>
                  <span className="text-cocoa font-medium">{u.email}</span>
                  <span className="text-cocoa/55 text-[11px] ml-2 font-mono">{u.id?.slice(0, 8)}</span>
                  {u.plan && <span className="text-mauve text-[11px] ml-2 uppercase tracking-widest">{u.plan}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {activeUser && (
        <div className="bg-ivory border border-champagne rounded-md p-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div><p className="text-[10px] tracking-[0.3em] uppercase text-mauve">Inspecting</p><p className="text-cocoa font-medium">{activeUser.email}</p></div>
            <button onClick={() => setActiveUser(null)} className="text-[12px] text-cocoa/55 hover:text-cocoa">Clear</button>
          </div>
          <UserInspection userId={activeUser.id} />
        </div>
      )}

      <BalancedGuidanceStats />
      <GlobalEffectiveness />
    </div>
  );
}

export default AdminIntelligenceConsole;
